import { restrictedCountryBanks } from 'data';

import { IRestrictedBanks } from '../../../../../types';
import { LoanType } from 'types';

type Fields = {
  loanType: string;
  loanDesc: string;
  country: string;
  bankName: string;
  loanName: string;
  sponsorshipAmount: number;
  personName: string;
  personFirstName: string;
  personSurname: string;
  personDesc: string;
  isOtherBankName: boolean;
};

type Visible = Record<
  | 'loanType'
  | 'loanDesc'
  | 'country'
  | 'bankName'
  | 'bankOtherName'
  | 'loanProvider'
  | 'loanName'
  | 'sponsorshipAmount'
  | 'personName'
  | 'personFirstName'
  | 'personSurname'
  | 'personDesc',
  boolean
>;

function isPersonGuardian(personName: string) {
  return ['Mother', 'Father', 'Guardian'].includes(personName);
}

export function isLoanTypeValid(loanType: string) {
  const arr: string[] = [LoanType.NATIONAL_GOV, LoanType.STATE_GOV, LoanType.LOAN_COMP, LoanType.LOAN_SCHEME];
  return arr.includes(loanType);
}

export function isLoanTypeUnknown(loanType: string) {
  const arr: string[] = [LoanType.UNKNOWN, LoanType.OTHER];
  return arr.includes(loanType);
}

export function isLoanTypeGeneralBank(loanType: string) {
  return loanType === 'A general bank loan (not specified as a student loan)';
}

function askRestrictedBank(loanType: string, country: string) {
  const arr: string[] = [LoanType.LOAN_COMP, LoanType.LOAN_SCHEME];
  return isRestrictedCountry(country) && arr.includes(loanType);
}

export function getCountryBanks(country: string): string[] {
  const banks = restrictedCountryBanks as IRestrictedBanks;
  const countryBanks = country ? banks[country] || [] : [];
  return countryBanks.map(x => x.bank);
}

export function isRestrictedCountry(country: string) {
  return getCountryBanks(country).length > 0;
}

export function getPersonNameString(personName: string) {
  switch (personName) {
    case 'Father':
      return 'Father';
    case 'Mother':
      return 'Mother';
    case 'Guardian':
      return 'Guardian';
    default:
      return '';
  }
}

export function loanTypeLabel(loanType: string) {
  switch (loanType) {
    case LoanType.NATIONAL_GOV:
      return 'Provided by a national government';
    case LoanType.STATE_GOV:
      return 'Provided by a state or regional government';
    case LoanType.LOAN_COMP:
      return 'Provided by a government-sponsored student loan company';
    case LoanType.LOAN_SCHEME:
      return 'Part of an academic or educational loans scheme';
    case LoanType.UNKNOWN:
      return "I don't know";
    case LoanType.OTHER:
      return 'Other';
    default:
      return '';
  }
}

export function fromLoanTypeLabel(loanType: string) {
  switch (loanType) {
    case 'Provided by a national government':
      return LoanType.NATIONAL_GOV;
    case 'Provided by a state or regional government':
      return LoanType.STATE_GOV;
    case 'Provided by a government-sponsored student loan company':
      return LoanType.LOAN_COMP;
    case 'Part of an academic or educational loans scheme':
      return LoanType.LOAN_SCHEME;
    case "I don't know":
      return LoanType.UNKNOWN;
    case 'Other':
      return LoanType.OTHER;
    default:
      return loanType;
  }
}

export function getPageStatus(fields: Fields): {
  complete: boolean;
  visible: Visible;
} {
  const {
    loanType,
    loanDesc,
    country,
    bankName,
    loanName,
    sponsorshipAmount,
    personName,
    personFirstName,
    personSurname,
    personDesc,
    isOtherBankName,
  } = fields;

  const complete: boolean = (() => {
    if (loanType === '') return false;

    if (isLoanTypeValid(loanType)) {
      if (country === '') return false;
      if (bankName === '') return false;
    }
    if (isLoanTypeUnknown(loanType)) {
      if (loanDesc === '') return false;
    }
    if (isLoanTypeValid(loanType) || isLoanTypeUnknown(loanType)) {
      if (loanName === '') return false;
      if (sponsorshipAmount === 0) return false;
      if (personName === '') return false;
      if (isPersonGuardian(personName) && (personFirstName === '' || personSurname === '')) return false;
      if (personName === 'Other' && personDesc === '') return false;
    }

    return true;
  })();

  const visible: Visible = {
    loanType: true,
    loanDesc: isLoanTypeUnknown(loanType),
    country: isLoanTypeValid(loanType),
    loanProvider: isLoanTypeValid(loanType) && !askRestrictedBank(loanType, country),
    bankName: isLoanTypeValid(loanType) && askRestrictedBank(loanType, country) && !isOtherBankName,
    bankOtherName: isLoanTypeValid(loanType) && askRestrictedBank(loanType, country) && isOtherBankName,
    loanName: isLoanTypeValid(loanType) || isLoanTypeUnknown(loanType),
    sponsorshipAmount: isLoanTypeValid(loanType) || isLoanTypeUnknown(loanType),
    personName: isLoanTypeValid(loanType) || isLoanTypeUnknown(loanType),
    personFirstName: (isLoanTypeValid(loanType) || isLoanTypeUnknown(loanType)) && isPersonGuardian(personName),
    personSurname: (isLoanTypeValid(loanType) || isLoanTypeUnknown(loanType)) && isPersonGuardian(personName),
    personDesc: (isLoanTypeValid(loanType) || isLoanTypeUnknown(loanType)) && personName === 'Other',
  };

  return {
    complete,
    visible,
  };
}
